import type { LineItem } from '../api'
import { DEFAULT_LINE_TAX_RATE } from '../../shared/constants'
import { COMPONENTS_LINE_ITEMS_EDITOR_TEXTS as T } from '../../shared/texts'

// Editable list of invoice/subscription line items, shared by both forms.
export default function LineItemsEditor({ items, onChange, defaultTaxRate }: {
    items: LineItem[]
    onChange: (items: LineItem[]) => void
    // Rate for a new line — the invoice form passes the reverse-charge decision's rate.
    defaultTaxRate?: number
}) {
    function update(i: number, patch: Partial<LineItem>) {
        onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))
    }
    function add() {
        onChange([...items, { description: '', quantity: 1, unitPrice: 0, taxRate: defaultTaxRate ?? DEFAULT_LINE_TAX_RATE }])
    }
    function remove(i: number) {
        onChange(items.filter((_, idx) => idx !== i))
    }

    // Mirrors the server's computeTotals EXACTLY: round each line's net, then its tax, then
    // sum. Summing untaxed and taxing the sum drifts a cent on multi-line invoices — the same
    // trap that made the Mollie checkout charge 38,01 € against a 38,00 € invoice (2026-08-06).
    // What is shown here must be what gets booked.
    const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100
    const totals = items.reduce((acc, it) => {
        const lineNet = round2(it.quantity * it.unitPrice)
        return { net: acc.net + lineNet, tax: acc.tax + round2((lineNet * it.taxRate) / 100) }
    }, { net: 0, tax: 0 })
    const net = round2(totals.net)
    const tax = round2(totals.tax)
    const gross = round2(net + tax)
    const fmt = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    return (
        <div>
            <table>
                <thead>
                    <tr>
                        <th>{T.description}</th>
                        <th className="num" style={{ width: 80 }}>{T.quantity}</th>
                        <th className="num" style={{ width: 120 }}>{T.unitPrice}</th>
                        <th className="num" style={{ width: 90 }}>{T.taxRate}</th>
                        <th style={{ width: 40 }}></th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((it, i) => (
                        <tr key={i}>
                            {/* Multi-line (operator, 31.10.2026): Enter starts a new line, which the PDF,
                                the credit note and the mail keep. As many rows as the text has lines. */}
                            <td><textarea value={it.description} rows={Math.max(1, it.description.split('\n').length)}
                                onChange={(e) => update(i, { description: e.target.value })} /></td>
                            <td className="num"><input type="number" value={it.quantity} style={{ width: 70 }}
                                onChange={(e) => update(i, { quantity: Number(e.target.value) })} /></td>
                            <td className="num"><input type="number" step="0.01" value={it.unitPrice} style={{ width: 110 }}
                                onChange={(e) => update(i, { unitPrice: Number(e.target.value) })} /></td>
                            <td className="num"><input type="number" value={it.taxRate} style={{ width: 70 }}
                                onChange={(e) => update(i, { taxRate: Number(e.target.value) })} /></td>
                            <td><button type="button" className="secondary" onClick={() => remove(i)}>✕</button></td>
                        </tr>
                    ))}
                    {items.length === 0 && (
                        <tr><td colSpan={5} className="muted">{T.empty}</td></tr>
                    )}
                </tbody>
            </table>
            <div className="row" style={{ marginTop: 10, justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <button type="button" className="secondary" onClick={add}>{T.add}</button>
                {/* Gross is what the customer actually paid — without it the form only ever
                    showed net, and a refund had to be judged from the wrong number. */}
                <div style={{ textAlign: 'right', lineHeight: 1.6 }}>
                    <div className="muted">{T.net}{fmt(net)}</div>
                    <div className="muted">{T.tax}{fmt(tax)}</div>
                    <div style={{ fontWeight: 600 }}>{T.gross}{fmt(gross)}</div>
                </div>
            </div>
        </div>
    )
}
